import { useMemo } from 'react';
import type { BetslipEvent } from '../../types/betslip';
import type { WalletType } from '../../types/enums';
import { getBetslipState, useBetslipStore } from './betslipStore';

/**
 * Per-outcome selection subscription for the hot path (outcome cells). Selects a boolean, so the
 * subscribing component re-renders only when this outcome's selection actually flips — not on
 * every betslip update. Matches `isOutcomeInBetslip` semantics (incl. bet-builder child outcomes).
 */
export const useIsOutcomeSelected = (outcomeId: string | undefined | null) =>
  useBetslipStore((state) => (outcomeId ? state.selectedOutcomeKeys.has(outcomeId) : false));

/** The betslip selections as an array (stable reference between bets commits). */
export const useBetslipSelections = (): BetslipEvent[] => {
  const bets = useBetslipStore((state) => state.bets);
  return useMemo(() => Object.values(bets), [bets]);
};

export const useBetslipCount = () => useBetslipStore((state) => state.betCount);
export const useBetslipTicketType = () => useBetslipStore((state) => state.betslipTicketType);
export const useBetslipUniqueEventCount = () => useBetslipStore((state) => state.uniqueEventCount);
export const useBetslipIsWaysTicket = () => useBetslipStore((state) => state.isWaysTicket);
export const useBetslipCurrentBankerCount = () => useBetslipStore((state) => state.currentBankerCount);
export const useBetslipIsMaxBankerCountReached = () => useBetslipStore((state) => state.isMaxBankerCountReached);
export const useBetslipHasBetBuilderSelection = () => useBetslipStore((state) => state.hasBetBuilderSelection);
export const useBetslipErrorKeys = () => useBetslipStore((state) => state.errorMessageKeys);
export const useBetslipInfoMessageKeys = () => useBetslipStore((state) => state.infoMessageKeys);
export const useBetslipReofferedIds = () => useBetslipStore((state) => state.reofferedBettingSlipsIds);
export const useBetslipSelectedWallet = () => useBetslipStore((state) => state.selectedWallet);

// Plain-function access to actions for components that only trigger betslip changes and don't
// need to re-render on betslip state: importing these creates no store subscription at all.
export const addBetslipBet = (bet: BetslipEvent) => getBetslipState().addBet(bet);
export const addOrRemoveBetslipBet = (bet: BetslipEvent) => getBetslipState().addOrRemoveBet(bet);
export const removeBetslipBet = (outcomeId: string) => getBetslipState().removeBet(outcomeId);
export const removeAllBetslipBets = () => getBetslipState().removeAllBets();
export const updateBetslipBetOdds = (...args: Parameters<ReturnType<typeof getBetslipState>['updateSingleBetOdds']>) =>
  getBetslipState().updateSingleBetOdds(...args);
export const toggleBetslipBanker = (outcomeId: string) => getBetslipState().toggleBanker(outcomeId);
export const clearBetslipErrors = () => getBetslipState().clearErrors();
export const resetBetslipBankers = () => getBetslipState().resetBankers();
export const insertCopiedBetslip = (bets: BetslipEvent[]) => getBetslipState().insertCopiedBetslip(bets);
export const updateBetslipSinglesStake = (outcomeId: string, amount: string) =>
  getBetslipState().updateSinglesStakeAmount(outcomeId, amount);
export const resetBetslipSinglesStakes = () => getBetslipState().resetSinglesStakeAmounts();
export const setBetslipSelectedWallet = (wallet: WalletType) => getBetslipState().setSelectedWallet(wallet);
export const removeReofferedBetslip = (bettingSlipId: string) =>
  getBetslipState().removeReofferedBettingSlip(bettingSlipId);
